/**
 * GlassButton - Glassmorphism-styled button component
 *
 * Features:
 * - Semi-transparent background with border
 * - Hover/press animations
 * - Glow effects
 * - Sound and haptic feedback
 * - Minimum touch target size (44x44px)
 */

import Phaser from 'phaser';
import { DURATION, EASING, TOUCH, TYPOGRAPHY } from '@/config/designSystem';
import { HapticManager } from '@/systems/HapticManager';

export interface GlassButtonConfig {
  text: string;
  fontSize?: number;
  width?: number;
  height?: number;
  style?: 'primary' | 'secondary';
  onClick?: () => void;
  iconKey?: string; // Optional icon texture key (e.g., 'icon-play-circle')
  iconSize?: number; // Optional icon size (default: fontSize * 1.2)
}

export class GlassButton extends Phaser.GameObjects.Container {
  private bg: Phaser.GameObjects.Rectangle;
  private border: Phaser.GameObjects.Rectangle;
  private label: Phaser.GameObjects.Text;
  private glow: Phaser.GameObjects.Rectangle;
  private icon?: Phaser.GameObjects.Image; // Optional icon sprite
  private config: GlassButtonConfig;
  private pointerDownOnButton: boolean = false; // Track if press started on this button

  constructor(scene: Phaser.Scene, x: number, y: number, config: GlassButtonConfig) {
    super(scene, x, y);
    this.config = config;

    // Calculate dimensions
    const width = config.width || 240;
    const height = config.height || Math.max(TOUCH.minSize, 60);
    const fontSize = config.fontSize || 24;
    const isPrimary = config.style === 'primary' || config.style === undefined;

    // Create glow effect (hidden by default)
    this.glow = scene.add.rectangle(0, 0, width + 8, height + 8, 0xffd700, 0);
    this.add(this.glow);

    // Create background with solid colors
    // Primary: Gold gradient, Secondary: Dark blue-gray
    const bgColor = isPrimary ? 0xffaa00 : 0x4a5568; // Gold or slate gray
    const bgAlpha = isPrimary ? 0.9 : 0.85;
    this.bg = scene.add.rectangle(0, 0, width, height, bgColor, bgAlpha);
    this.add(this.bg);

    // Create border (invisible, just for container)
    this.border = scene.add.rectangle(0, 0, width, height);
    this.border.setFillStyle(0x000000, 0); // Transparent fill
    this.add(this.border);

    // Create icon if provided
    const iconSize = config.iconSize || fontSize * 1.2;
    const hasIcon = !!config.iconKey;

    if (hasIcon && config.iconKey) {
      this.icon = scene.add.image(0, 0, config.iconKey);
      this.icon.setDisplaySize(iconSize, iconSize);
      this.icon.setOrigin(0.5);
      this.add(this.icon);
    }

    // Create text label - white for both styles
    const textColor = '#FFFFFF';
    this.label = scene.add.text(0, 0, config.text, {
      fontSize: `${fontSize}px`,
      color: textColor,
      fontFamily: TYPOGRAPHY.fonts.josefin, // No longer need Material Symbols fallback
      fontStyle: 'bold',
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    this.label.setOrigin(0.5);
    this.add(this.label);

    // Position icon and text
    if (hasIcon && this.icon) {
      const spacing = 8; // Space between icon and text
      const contentWidth = iconSize + spacing + this.label.width;
      this.icon.setX(-contentWidth / 2 + iconSize / 2);
      this.label.setX(-contentWidth / 2 + iconSize + spacing + this.label.width / 2);
    }

    // Make interactive
    this.setupInteractivity(width, height);

    // Add to scene
    scene.add.existing(this);
  }

  private setupInteractivity(width: number, height: number): void {
    // Set up hitbox (minimum 44x44px for touch)
    const hitWidth = Math.max(width, TOUCH.minSize);
    const hitHeight = Math.max(height, TOUCH.minSize);

    this.setSize(hitWidth, hitHeight);
    this.setInteractive({ useHandCursor: true });

    // Hover effect
    this.on('pointerover', () => {
      this.onHover();
    });

    this.on('pointerout', () => {
      this.onHoverExit();
      // Reset flag if user drags away from button
      this.pointerDownOnButton = false;
    });

    // Press effect - only on this button
    this.on('pointerdown', () => {
      this.pointerDownOnButton = true; // Mark that press started here
      this.onPress();
      // Haptic feedback on button tap
      HapticManager.buttonTap();
    });

    // Release effect - only trigger onClick if press started on this button
    this.on('pointerup', () => {
      this.onRelease();
      // Only execute onClick if the pointer was pressed down on this button
      if (this.pointerDownOnButton && this.config.onClick) {
        this.config.onClick();
      }
      // Reset flag after handling
      this.pointerDownOnButton = false;
    });
  }

  private onHover(): void {
    const isPrimary = this.config.style === 'primary' || this.config.style === undefined;

    // Scale up slightly
    this.scene.tweens.add({
      targets: this,
      scale: 1.05,
      duration: DURATION.fast,
      ease: EASING.easeOut,
    });

    // Brighten background
    this.scene.tweens.add({
      targets: this.bg,
      alpha: isPrimary ? 1.0 : 0.95,
      duration: DURATION.fast,
    });

    // Show glow
    this.scene.tweens.add({
      targets: this.glow,
      alpha: 0.4,
      duration: DURATION.fast,
    });
  }

  private onHoverExit(): void {
    const isPrimary = this.config.style === 'primary' || this.config.style === undefined;

    // Scale back to normal
    this.scene.tweens.add({
      targets: this,
      scale: 1.0,
      duration: DURATION.fast,
      ease: EASING.easeOut,
    });

    // Restore background
    this.scene.tweens.add({
      targets: this.bg,
      alpha: isPrimary ? 0.9 : 0.85,
      duration: DURATION.fast,
    });

    // Hide glow
    this.scene.tweens.add({
      targets: this.glow,
      alpha: 0,
      duration: DURATION.fast,
    });
  }

  private onPress(): void {
    const isPrimary = this.config.style === 'primary' || this.config.style === undefined;

    // Scale down (press effect)
    this.scene.tweens.add({
      targets: this,
      scale: 0.95,
      duration: DURATION.instant,
      ease: EASING.easeIn,
    });

    // Darken background
    this.scene.tweens.add({
      targets: this.bg,
      alpha: isPrimary ? 0.7 : 0.65,
      duration: DURATION.instant,
    });
  }

  private onRelease(): void {
    const isPrimary = this.config.style === 'primary' || this.config.style === undefined;

    // Bounce back
    this.scene.tweens.add({
      targets: this,
      scale: 1.05,
      duration: DURATION.fast,
      ease: EASING.bounceOut,
    });

    // Brighten background (hover state)
    this.scene.tweens.add({
      targets: this.bg,
      alpha: isPrimary ? 1.0 : 0.95,
      duration: DURATION.fast,
    });
  }

  /**
   * Update button text
   */
  setText(text: string): this {
    this.label.setText(text);
    return this;
  }

  /**
   * Enable/disable button
   */
  setEnabled(enabled: boolean): this {
    this.setInteractive(enabled);
    this.setAlpha(enabled ? 1.0 : 0.5);
    return this;
  }
}
